// services/ProfileManager.js
import { Profile } from '../models';
import { createLogger } from './Debug';
export class ProfileManager {
  constructor() {
    this.profile = null;
    this.refreshPromise = null;
    this.userService = null; // Will be set by userService to avoid circular dependency
  }

  
  loadFromAPI(apiResponse) {
    this.profile = new Profile(apiResponse);
    this.saveToStorage();
    // Notify listeners in-app that profile changed
    try {
      window.dispatchEvent(new Event('profileUpdated'));
    } catch (_) {}
    return this.profile;
  }

  // Get current profile (from memory or storage)
  getCurrentProfile() {
    
    if (this.profile && this.isCacheValid()) {
      return this.profile;
    }
    
    const storedProfile = this.loadFromStorage();
    
    // If we have a stored profile, check if it's expired
    if (storedProfile && storedProfile.expiresAt) {
      const isExpired = new Date(storedProfile.expiresAt) < new Date();
      
      if (isExpired && !this.refreshPromise) {
        // Return the expired profile but refresh in background
        this.refreshInBackground();
      }
    } 
    
    return storedProfile;
  }

  // Check if cache is still valid
  isCacheValid() {
    if (!this.profile) return false;
    
    const now = new Date();
    const expiresAt = new Date(this.profile.expiresAt);
    return expiresAt > now;
  }

  // Refresh profile in background
  async refreshInBackground() {
    if (this.refreshPromise || !this.userService) return;
    
    this.refreshPromise = (async () => {
      try {
        const freshProfile = await this.userService.getProfile();
        this.loadFromAPI(freshProfile);
      } catch (error) {
        const log = createLogger('ProfileManager.refreshInBackground');
        log.error('Failed to refresh profile in background:', error);
      } finally {
        this.refreshPromise = null;
      }
    })();
  }

  // Save to localStorage
  saveToStorage() {
    if (!this.profile) return;
    
    // Save complete profile
    localStorage.setItem('userProfile', JSON.stringify(this.profile.toJSON()));
    
  }

  // Load from localStorage
  loadFromStorage() {
    const stored = localStorage.getItem('userProfile');
    if (!stored) return null;
    
    try {
      const data = JSON.parse(stored);
      this.profile = Profile.fromJSON(data);
      return this.profile;
    } catch (error) {
      const log = createLogger('ProfileManager.loadFromStorage');
      log.error('Error loading profile from storage:', error);
      return null;
    }
  }


  // Update profile partially
  updateProfile(updates) {
    if (!this.profile) return;
    
    // Apply updates to appropriate sub-models
    if (updates.user) {
      Object.assign(this.profile.user, updates.user);
    }
    if (updates.profile) {
      Object.assign(this.profile.user, updates.profile);
    }
    if (updates.reportTypeNames) {
      // Handle ReportTypeNames object update
      this.profile.reportTypeNames = updates.reportTypeNames;
    }
    if (updates.reportTypeFavorites) {
      // Handle ReportTypeFavorites object update
      this.profile.reportTypeFavorites = updates.reportTypeFavorites;
    }
    if (updates.macros) {
      this.profile.macros = updates.macros;
    }
    if (updates.tutorials) {
      // Update tutorials object
      Object.assign(this.profile.tutorials, updates.tutorials);
    }
    
    this.saveToStorage();
    
    // Emit profile update event
    window.dispatchEvent(new Event('profileUpdated'));
    
    return this.profile;
  }

  // Update cache with a profile instance
  updateCache(profile) {
    this.profile = profile;
    this.saveToStorage();
  }

  // Clear profile
  clear() {
    this.profile = null;
    localStorage.removeItem('userProfile');
  }
}